import { useEffect, type RefObject } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import {
  Button,
  DialogClose,
  Field,
  Form,
  FormActions,
  FormDrawer,
  FormError,
  Input,
  Textarea,
} from "@h2j-data-team/ui";

import { createGroupInputSchema, type CreateGroupInput } from "@repo/shared";
import { getErrorMessage } from "@/lib/api-client";
import { discardMessages } from "@/lib/ui-labels";
import { useCreateGroup } from "@/features/groups/api";

const FORM_ID = "create-sync-group-form";

export function CreateSyncGroupDrawer({
  open,
  onOpenChange,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The control that opened the drawer: focus goes back to it on close. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation("sync-groups");
  const { t: tc } = useTranslation("common");
  const navigate = useNavigate();
  const createMutation = useCreateGroup();

  const form = useForm<CreateGroupInput>({
    resolver: zodResolver(createGroupInputSchema),
    defaultValues: { name: "", description: "" },
  });

  // Reset to a clean state each time the drawer opens.
  useEffect(() => {
    if (open) {
      form.reset({ name: "", description: "" });
      createMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = async (data: CreateGroupInput) => {
    try {
      const created = await createMutation.mutateAsync(data);
      onOpenChange(false);
      navigate({ to: "/sync-groups/$groupId", params: { groupId: created.id } });
    } catch {
      // surfaced via createMutation.error
    }
  };

  const { errors, isDirty } = form.formState;

  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      closeLabel={tc("close")}
      title={t("new.pageTitle")}
      description={t("new.description")}
      restoreFocusRef={restoreFocusRef}
      busy={createMutation.isPending}
      dirty={isDirty}
      discardConfirmation={discardMessages()}
      footer={
        <FormActions
          busy={createMutation.isPending}
          submitLabel={t("new.create")}
          submittingLabel={t("new.creating")}
          submitProps={{ form: FORM_ID }}
        >
          <DialogClose asChild>
            <Button variant="outline">{t("new.cancel")}</Button>
          </DialogClose>
        </FormActions>
      }
    >
      <Form id={FORM_ID} busy={createMutation.isPending} onSubmit={form.handleSubmit(onSubmit)}>
        <Field label={t("new.nameLabel")} error={errors.name?.message}>
          <Input placeholder={t("new.namePlaceholder")} {...form.register("name")} />
        </Field>

        <Field label={t("new.descriptionLabel")} error={errors.description?.message}>
          <Textarea
            placeholder={t("new.descriptionPlaceholder")}
            rows={3}
            {...form.register("description")}
          />
        </Field>

        {createMutation.error && <FormError>{getErrorMessage(createMutation.error)}</FormError>}
      </Form>
    </FormDrawer>
  );
}
